<template>
  <div class="relative">
    <button 
      @click="$emit('toggle')"
      class="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors duration-200"
    >
      <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
      </svg>
    </button>
    
    <!-- Quick Actions Dropdown -->
    <div 
      v-if="isOpen && hasAnyQuickAction"
      class="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50"
    >
      <div class="px-4 py-2 border-b border-gray-100">
        <h3 class="text-sm font-medium text-gray-900">Actions rapides</h3>
      </div>
      <div class="py-1">
        <!-- Nouvel employé -->
        <button 
          v-if="canCreateEmployee"
          @click="$emit('action', 'new-employee')"
          class="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors duration-200"
        >
          <svg class="w-4 h-4 mr-3 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z"></path>
          </svg>
          Nouvel employé
        </button>
        
        <!-- Demande d'absence -->
        <button 
          v-if="canCreateAbsence"
          @click="$emit('action', 'new-absence')"
          class="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors duration-200"
        >
          <svg class="w-4 h-4 mr-3 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
          </svg>
          Demande d'absence
        </button>
        
        <!-- Note de frais -->
        <button 
          v-if="canCreateExpense"
          @click="$emit('action', 'new-expense')"
          class="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors duration-200"
        >
          <svg class="w-4 h-4 mr-3 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
          </svg>
          Note de frais
        </button>
        
        <!-- Nouveau document -->
        <button 
          v-if="canUploadFile"
          @click="$emit('action', 'new-document')"
          class="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors duration-200"
        >
          <svg class="w-4 h-4 mr-3 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"></path>
          </svg>
          Nouveau document
        </button>
        
        <!-- Nouveau tableau de bord -->
        <button 
          v-if="canCreateDashboard"
          @click="$emit('action', 'new-dashboard')"
          class="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors duration-200"
        >
          <svg class="w-4 h-4 mr-3 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"></path>
          </svg>
          Nouveau tableau de bord
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useAuthStore } from '@/stores/auth.store'
import { PERMISSIONS } from '@/constants/permissions'

interface Props {
  isOpen: boolean
}

defineProps<Props>()

defineEmits<{
  toggle: []
  action: [action: string]
}>()

const authStore = useAuthStore()

// Vérifications de permissions pour les actions rapides
const canCreateEmployee = computed(() => 
  authStore.hasPermission(PERMISSIONS.EMPLOYEE.CREATE)
)

const canCreateAbsence = computed(() => 
  authStore.hasPermission(PERMISSIONS.ABSENCE.CREATE)
)

const canCreateExpense = computed(() => 
  authStore.hasPermission(PERMISSIONS.EXPENSE.CREATE)
)

const canUploadFile = computed(() => 
  authStore.hasPermission(PERMISSIONS.FILE.UPLOAD)
)

const canCreateDashboard = computed(() => 
  authStore.hasPermission(PERMISSIONS.DASHBOARD.CREATE)
)

// Vérifier s'il y a au moins une action rapide disponible
const hasAnyQuickAction = computed(() => 
  canCreateEmployee.value || 
  canCreateAbsence.value || 
  canCreateExpense.value || 
  canUploadFile.value || 
  canCreateDashboard.value
)
</script>
